import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";

interface Analytics {
  total_gigs: number;
  open_gigs: number;
  completed_gigs: number;
  total_payout_cents: number;
  urgent_count: number;
  top_tag: string;
  top_tag_count: number;
}

interface Report {
  id: string;
  gig_id: string | null;
  reporter_id: string;
  reported_user: string | null;
  reason: string;
  details: string | null;
  status: string;
  created_at: string;
}

const AdminHustleHub = () => {
  const [stats, setStats] = useState<Analytics | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [a, r] = await Promise.all([
      supabase.rpc("get_gig_analytics"),
      supabase
        .from("gig_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    if (a.error) toast.error(a.error.message);
    else if (Array.isArray(a.data) && a.data[0]) setStats(a.data[0] as Analytics);
    if (r.error) toast.error(r.error.message);
    else setReports((r.data ?? []) as Report[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (id: string) => {
    const { error } = await supabase
      .from("gig_reports")
      .update({ status: "resolved", resolved_at: new Date().toISOString() })
      .eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Marked resolved");
      load();
    }
  };

  if (loading) {
    return (
      <div className="p-6 grid place-items-center min-h-[40vh] text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Hustle Hub</h1>
          <p className="text-sm text-muted-foreground">Gig analytics &amp; reports</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase text-muted-foreground">Total gigs</div>
              <div className="text-2xl font-bold">{stats.total_gigs}</div>
              <div className="text-xs text-muted-foreground">{stats.open_gigs} open</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase text-muted-foreground">Completed</div>
              <div className="text-2xl font-bold">{stats.completed_gigs}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase text-muted-foreground">Payout volume</div>
              <div className="text-2xl font-bold">{formatMoney(stats.total_payout_cents)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase text-muted-foreground">Urgent / Now</div>
              <div className="text-2xl font-bold">{stats.urgent_count}</div>
              <div className="text-xs text-muted-foreground">
                Top tag: {stats.top_tag} ({stats.top_tag_count})
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Recent reports
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {reports.length === 0 && (
            <p className="text-sm text-muted-foreground">No reports.</p>
          )}
          {reports.map((r) => (
            <div
              key={r.id}
              className="rounded-lg border border-border p-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={r.status === "open" ? "destructive" : "secondary"}>
                    {r.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm font-medium">{r.reason}</div>
                {r.details && (
                  <div className="text-xs text-muted-foreground mt-1">{r.details}</div>
                )}
                {r.gig_id && (
                  <a
                    href={`/gigs/${r.gig_id}`}
                    className="text-xs text-primary hover:underline"
                  >
                    View gig
                  </a>
                )}
              </div>
              {r.status === "open" && (
                <Button size="sm" variant="outline" onClick={() => resolve(r.id)}>
                  Resolve
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHustleHub;
